import React, { useEffect, useState } from 'react'
import { CommonPlaceholder } from '../../../common/CommonPlaceholder'
import { SpinLoader } from '../../../common/SpinLoader'

import { UserRow } from '../UserRow'
import './index.less'
import { DecryptedPatient, HealthcareParty } from '@icure/cardinal-sdk'
import { createPortal } from 'react-dom'

import { ModalConfirmAction } from '../../../common/ModalConfirmAction'

import { ModalAddConsultationForm } from '../../modals/ModalAddConsultationForm'
import { ModalPatientForm } from '../../modals/ModalPatientForm'
import { ModalPatientProfile } from '../../modals/ModalPatientProfile'
import { ModalSharePatient } from '../../modals/ModalSharePatient'
import { useDeletePatientMutation } from '../../../../core/api/patientApi'
import { message, notification } from 'antd'

interface UserTableProps {
  healthcareParty: HealthcareParty | undefined
  showSpinner: boolean
  patientList?: DecryptedPatient[]
  selectedPatients?: string[]
  onPatientSelect: (selectedPatient: DecryptedPatient, checked: boolean) => void
  noMatchingPatientsPlaceholder?: boolean
  patientsWithUserAccount?: ReadonlySet<string>
}

export const UserTable = ({ healthcareParty, showSpinner, patientList, selectedPatients, onPatientSelect, noMatchingPatientsPlaceholder, patientsWithUserAccount }: UserTableProps) => {
  const [isPatientFormModalOpen, setPatientFormModalOpen] = useState(false)
  const [isAddConsultationModalOpen, setAddConsultationModalOpen] = useState(false)
  const [isPatientProfileModalOpen, setPatientProfileModalOpen] = useState(false)
  const [isPatientToBeDeleted, setPatientToBeDeleted] = useState(false)
  const [isSharePatientModalOpen, setSharePatientModalOpen] = useState(false)

  const [actionedPatientId, setActionedPatientId] = useState<string | undefined>(undefined)

  const [deletePatient, { isError: isDeletePatientError, isSuccess: isPatientDeletedSuccessfully, isLoading: isPatientDeletingLoading }] = useDeletePatientMutation()

  const [notificationApi] = notification.useNotification()
  const [messageApi] = message.useMessage()

  const openNotification = (type: 'error', message: string, description: string) => {
    notificationApi.open({
      type,
      message,
      description,
      duration: 0,
    })
    setTimeout(notificationApi.destroy, 2500)
  }

  const showMessageFeedback = (type: 'loading' | 'success' | 'error', content: string) => {
    messageApi.open({
      type,
      content,
      duration: 0,
    })
    // Dismiss manually and asynchronously
    setTimeout(messageApi.destroy, type === 'loading' ? 100 : 2500)
  }

  useEffect(() => {
    if (isPatientDeletingLoading) showMessageFeedback('loading', 'The patient is deleting...')
    if (isPatientDeletedSuccessfully) showMessageFeedback('success', 'The patient was deleted!')
    if (isDeletePatientError) openNotification('error', 'We could not delete the patient!', `An error occurred while deleting the patient`)
  }, [isPatientDeletingLoading, isPatientDeletedSuccessfully, isDeletePatientError])

  const onRowAction = ({ patientId, key }: { patientId: string; key: string }) => {
    switch (key) {
      case 'add_consultation': {
        setActionedPatientId(patientId)
        setAddConsultationModalOpen(true)
        break
      }
      case 'view_profile': {
        setActionedPatientId(patientId)
        setPatientProfileModalOpen(true)
        break
      }
      case 'edit_patient': {
        setActionedPatientId(patientId)
        setPatientFormModalOpen(true)
        break
      }
      case 'share_patient': {
        setActionedPatientId(patientId)
        setSharePatientModalOpen(true)
        break
      }
      case 'delete_patient': {
        setActionedPatientId(patientId)
        setPatientToBeDeleted(true)
        break
      }
    }
  }
  const patient = patientList?.find((p) => p.id === actionedPatientId)
  return (
    <div className="userTable">
      {showSpinner && <SpinLoader />}
      {patientList && patientList?.length !== 0 ? (
        patientList?.map((patient: DecryptedPatient) => {
          return (
            <UserRow
              key={patient.id}
              healthcareParty={healthcareParty}
              patient={patient}
              isSelected={selectedPatients?.some((pId) => pId === patient.id)}
              hasUserAccount={patientsWithUserAccount?.has(patient.id) ?? false}
              onSelect={(checked: boolean) => onPatientSelect(patient, checked)}
              onAction={(key: string) => onRowAction({ patientId: patient.id, key })}
            />
          )
        })
      ) : (
        <CommonPlaceholder
          title={noMatchingPatientsPlaceholder ? 'No Patients Found' : 'No Patients Added Yet'}
          content={
            noMatchingPatientsPlaceholder
              ? 'Try adjusting your search criteria or adding a new patient.'
              : "It looks like you haven't added any patients to your list. Don't worry, you can easily add them by clicking the 'Add' or 'Import' button."
          }
        />
      )}
      {isPatientFormModalOpen &&
        patient &&
        createPortal(<ModalPatientForm mode="edit" patientToEdit={patient} isVisible={isPatientFormModalOpen} onClose={() => setPatientFormModalOpen(false)} />, document.body)}
      {isAddConsultationModalOpen &&
        patient &&
        createPortal(<ModalAddConsultationForm isVisible={isAddConsultationModalOpen} onClose={() => setAddConsultationModalOpen(false)} patient={patient} />, document.body)}
      {isPatientProfileModalOpen &&
        actionedPatientId &&
        createPortal(
          <ModalPatientProfile
            patientId={actionedPatientId}
            isVisible={isPatientProfileModalOpen}
            onClose={() => setPatientProfileModalOpen(false)}
            onEdit={() => setPatientFormModalOpen(true)}
            onAddConsultation={() => setAddConsultationModalOpen(true)}
          />,
          document.body,
        )}
      {isPatientToBeDeleted &&
        patient &&
        createPortal(
          <ModalConfirmAction
            title="Delete patient"
            description="Are you sure you want to delete this patient? Once deleted, their information can't be recovered, so it's a permanent action."
            yesBtnTitle="Delete"
            noBtnTitle="Cancel"
            onYesClick={() => {
              deletePatient(patient)
              setPatientToBeDeleted(false)
            }}
            onNoClick={() => setPatientToBeDeleted(false)}
            isVisible={isPatientToBeDeleted}
            mode="danger"
          />,
          document.body,
        )}
      {isSharePatientModalOpen &&
        patient &&
        createPortal(<ModalSharePatient isVisible={isSharePatientModalOpen} onClose={() => setSharePatientModalOpen(false)} patientToEdit={patient} />, document.body)}
    </div>
  )
}
